import * as React from 'react';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { Icon } from '../icon/Icon';
import { Button } from '../button/Button';
import { ReactNode, useEffect, useRef } from 'react';
import { StringInput } from '../string/StringInput';
import { List } from '../list/List';
import { ListItem } from '../list/ListItem';
import { mcClose, mcSearch } from '@mc/react-ui-icons';
import { Title } from '../typography/Title';

const DEFAULT_EMPTY = {
  id: null,
  name: 'Prázdná hodnota',
};

export const defaultMultiSelectRenderItem: (item: MultiSelectItem) => string = (
  i
) => {
  if (typeof i.name === 'string') {
    return i.name;
  }
  if (typeof i.nazev === 'string') {
    return i.nazev;
  } else {
    return '';
  }
};

export const defaultMultiSelectGetLabel: (item: MultiSelectItem) => string = (
  i
) => {
  if (typeof i.name === 'string') {
    return i.name;
  }
  if (typeof i.nazev === 'string') {
    return i.nazev;
  } else {
    return '';
  }
};

export interface MultiSelectItem {
  id: any | null;
  name?: string | ReactNode;
  nazev?: string | ReactNode;
}

type Props<ID> = {
  disabled?: boolean;
  items: MultiSelectItem[];
  getLabel?: (item: MultiSelectItem) => string;
  renderItem?: (item: MultiSelectItem) => ReactNode;
  onChange: (val: ID[]) => void;
  value: ID[];
  className?: string;
  getSearchableFn?: (i: MultiSelectItem) => string;
  canBeEmpty?: boolean | MultiSelectItem;
  title?: ReactNode;
  closePopup?: () => void;
  /** Schová search input, dočasné řešení bugu*/
  hideSearch?: boolean;
  hideCloseButton?: boolean;
  noAutoFocus?: boolean;
};

export function MultiSelectSearch<ID>({
  disabled,
  items,
  onChange,
  noAutoFocus,
  value = [],
  getLabel = defaultMultiSelectGetLabel,
  className,
  canBeEmpty = false,
  hideSearch,
  title,
  renderItem,
  closePopup,
  hideCloseButton,
}: Props<ID>) {
  const [search, setSearch] = React.useState('');
  let srchVal = search
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
  const fixedVal = value !== undefined ? value : [];

  const disableKeyboard = true;

  let realItems = items;
  if (srchVal && srchVal.length > 0) {
    realItems = realItems.filter((i) => {
      const searchIn: string = getLabel(i);
      return (
        searchIn &&
        searchIn.length > 0 &&
        searchIn
          .toLowerCase()
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .indexOf(srchVal) !== -1 /* || fixedVal.indexOf(i.id) !== -1*/
      );
    });
  } else if (canBeEmpty) {
    if (canBeEmpty === true) {
      realItems = [DEFAULT_EMPTY, ...realItems];
    } else {
      realItems = [canBeEmpty, ...realItems];
    }
  }

  const keyHand = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (disableKeyboard) {
        return;
      }

      if (e.key == 'ArrowUp') {
        // setActiveIndex((act) => (act - 1 >= 0 ? act - 1 : 0));
      } else if (e.key === 'ArrowDown') {
        // setActiveIndex((act) =>
        //   act + 1 <= realItems.length - 1 ? act + 1 : realItems.length - 1
        // );
      } else if (e.key === 'PageUp') {
        if (e.altKey) {
        } else {
          // setActiveIndex((act) => (act - 10 >= 0 ? act - 10 : 0));
        }
      } else if (e.key === 'PageDown') {
        if (e.altKey) {
          // setActiveIndex((act) =>
          //   act + 10 <= realItems.length - 10 ? act + 10 : realItems.length - 1
          // );
        } else {
          // setActiveIndex((act) =>
          //   act + 10 <= realItems.length - 10 ? act + 10 : realItems.length - 1
          // );
        }
      } else if (e.key === 'Enter') {
        // setActiveIndex((activeIndex) => {
        //   const item = realItems[activeIndex];
        //   const id = item.id;
        //   const index = fixedVal.indexOf(id);
        //   onChange(
        //     index !== -1
        //       ? [...fixedVal.slice(0, index), ...fixedVal.slice(index + 1)]
        //       : [...fixedVal, id]
        //   );
        //   return activeIndex;
        // });
      }
    },
    [realItems, fixedVal]
  );

  const selectAll = React.useCallback(() => {
    onChange(Array.from(new Set([...fixedVal, ...realItems.map((i) => i.id)])));
  }, [fixedVal, realItems]);
  const unSellectAll = React.useCallback(() => {
    onChange(
      fixedVal.filter((id) => !realItems.map((item) => item.id).includes(id))
    );
  }, [fixedVal, realItems]);

  const inputRef = useRef<any>(undefined);

  // Pokud focus nastavíme bez timeoutu, je z nějakého neznámého důvodu rozmazaný font v celém popup okně - ale pouze v Chromium
  useEffect(() => {
    setTimeout(() => {
      if (!noAutoFocus) {
        inputRef.current.focus();
      }
    }, 1);
  }, [inputRef.current, noAutoFocus]);

  const realSelectedItems = React.useMemo(
    () => realItems.filter((x) => fixedVal.includes(x.id)),
    [items, realItems, fixedVal]
  );
  const realUnselectedItems = React.useMemo(
    () => realItems.filter((x) => !fixedVal.includes(x.id)),
    [items, realItems, fixedVal]
  );

  return (
    <div className={classNames(className, Classes.MULTI_SELECT_SEARCH)}>
      <Title
        className={Classes.MULTI_SELECT_SEARCH_TITLE}
        extra={
          !hideCloseButton && (
            <Button
              onClick={closePopup}
              style={'link'}
              icon={<Icon icon={mcClose} />}
            />
          )
        }
        type={'card'}
      >
        {title}
      </Title>
      {!hideSearch && (
        <StringInput
          disabled={disabled}
          inputRef={inputRef}
          placeholder="Vyhledat"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearch(e.target.value);
          }}
          rightElement={<Icon fixedWidth icon={mcSearch} />}
          onKeyDown={keyHand}
          className={classNames(Classes.MULTI_SELECT_SEARCH_INPUT)}
          inputClassName={Classes.FORM_NO_FORM_INPUT}
        />
      )}
      <div className={Classes.MULTI_SELECT_SEARCH_BUTTONS}>
        <Button
          style={'link'}
          onClick={selectAll}
          disabled={realSelectedItems.length === realItems.length || disabled}
        >
          Vybrat vše
        </Button>
        <Button
          style={'link'}
          onClick={unSellectAll}
          disabled={realUnselectedItems.length === realItems.length || disabled}
        >
          Odebrat vše
        </Button>
      </div>
      <div className={Classes.MULTI_SELECT_SEARCH_ITEMS_CONTAINER}>
        <List mode={'input'} compact>
          {realItems.length === 0 && (
            <div className={'p-2'}>
              Dle zadaného filtru nebyly nalezeny žádné položky
            </div>
          )}
          {realItems.map((item) => {
            const id = item.id;
            const index = fixedVal.indexOf(id);
            const selected = index !== -1;
            const onSelect = disabled
              ? undefined
              : selected
                ? () =>
                    onChange([
                      ...fixedVal.slice(0, index),
                      ...fixedVal.slice(index + 1),
                    ])
                : () => onChange([...fixedVal, id]);
            return (
              <ListItem
                disabled={disabled}
                // focus={!disableKeyboard && focus && activeIndex === listIndex}
                rowCheck
                checkable
                checked={selected}
                onCheck={onSelect}
              >
                {renderItem ? renderItem(item) : getLabel(item)}
              </ListItem>
            );
          })}
        </List>
      </div>
    </div>
  );
}
